<script lang="ts">
  /**
   * Who bowls the next over.
   *
   * The engine hands over only the bowlers who may legally bowl it — quota not
   * spent, and not the man who bowled the previous over. Everyone else in the
   * XI is listed too, with the reason they cannot, because a bowler silently
   * missing from the list reads as the app forgetting them.
   */
  import type { Match } from '../lib/match.svelte';
  import { figures } from '../lib/commentary';
  import { roleLabel, tier } from '../lib/format';
  import type { Player } from '@ipl-sim/engine-cricket';

  interface Props {
    match: Match;
  }
  let { match }: Props = $props();

  const prompt = $derived(match.prompt?.kind === 'SELECT_BOWLER' ? match.prompt : null);
  const eligibleIds = $derived(new Set(prompt?.eligible.map((p) => p.id) ?? []));

  interface Row {
    player: Player;
    ok: boolean;
    why: string;
    note: string;
  }

  /**
   * Everyone in the XI who has ever bowled, eligible first. `bowlOvers` decides
   * that, never the role label — the source data types R Ashwin and Sam Curran
   * as "BAT".
   */
  const rows = $derived.by<Row[]>(() => {
    if (!prompt) return [];
    const out: Row[] = [];
    for (const b of match.bowling) {
      if (b.player.bowlOvers < 1) continue;
      const cap = Math.min(4, b.player.bowlOvers);
      const oversBowled = Math.floor(b.balls / 6);
      const ok = eligibleIds.has(b.player.id);
      const spent = oversBowled >= cap;
      out.push({
        player: b.player,
        ok,
        why: ok ? '' : spent ? 'Quota complete' : 'Bowled the previous over',
        note: b.balls
          ? `${cap - oversBowled} over${cap - oversBowled === 1 ? '' : 's'} left · ${figures(b.balls, b.runs, b.wickets)}`
          : `${cap} over${cap === 1 ? '' : 's'} available`,
      });
    }
    return out.sort((a, b) => Number(b.ok) - Number(a.ok) || b.player.bowl - a.player.bowl);
  });
</script>

<section class="screen active" aria-label="Choose bowler">
  <div class="scorebar">
    <div class="scoreline">
      <span class="runs tabular">{match.runs}</span>
      <span class="wk tabular">/{match.wickets}</span>
      <span class="ov tabular">{match.overs}</span>
    </div>
    <div class="teams">
      <span class="opp-team">{match.battingSideName}</span>
      <span class="muted">
        {20 - (prompt?.overIndex ?? 0)} over{20 - (prompt?.overIndex ?? 0) === 1 ? '' : 's'} left
      </span>
    </div>
  </div>

  <div class="body">
    <p class="eyebrow">Over {(prompt?.overIndex ?? 0) + 1} — who bowls?</p>

    {#each rows as row (row.player.id)}
      <button
        class="prow"
        class:blocked={!row.ok}
        data-role={row.player.role}
        disabled={!row.ok}
        onclick={() => match.selectBowler(row.player.id)}
      >
        <span>
          <span class="nm">
            {row.player.name}
            {#if !row.player.isIndian}
              <svg class="plane" aria-hidden="true"><use href="#i-plane" /></svg>
              <span class="sr">Overseas</span>
            {/if}
          </span>
          <span class="sub">
            <span class="tagrole">{roleLabel(row.player.role)}</span>
            <span class="pos">{row.note}</span>
          </span>
        </span>
        <span class="figs">
          <span class="fig" data-t={tier(row.player.bowl)}><b>{row.player.bowl}</b><s>BOWL</s></span>
        </span>
        {#if !row.ok}<span class="why">{row.why}</span>{/if}
      </button>
    {/each}
  </div>
</section>
